import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../ui/AnimatedSection';

export const About: React.FC = () => {
  return (
    <section id="about" className="py-20 bg-slate-50 dark:bg-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-2 h-2 bg-indigo-400 rounded-full opacity-20 animate-float-dots" />
        <div className="absolute top-1/4 right-20 w-1 h-1 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
             style={{ animationDelay: '-3s' }} />
        <div className="absolute bottom-1/3 left-1/4 w-1.5 h-1.5 bg-indigo-500 rounded-full opacity-10 animate-float-dots" 
             style={{ animationDelay: '-6s' }} />
        
        {/* Floating lines */}
        <div className="absolute top-1/3 right-0 h-px w-32 bg-gradient-to-l from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines" />
        <div className="absolute bottom-1/4 left-0 h-px w-24 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines" 
             style={{ animationDelay: '-4s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              À propos de moi
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-primary-500 mx-auto"></div>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <AnimatedSection direction="left" delay={0.2}>
            <div className="space-y-6">
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                Passionné par le développement web, je serai dès septembre étudiant en 
                <span className="font-semibold text-indigo-600 dark:text-indigo-400"> Mastère Dev Manager Fullstack</span> à 
                EFREI Paris. Avec une expérience pratique en React et une solide base en développement Full Stack, 
                je cherche à appliquer mes compétences techniques tout en continuant à apprendre et à évoluer 
                dans un environnement dynamique.
              </p>
              
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                Je suis activement à la recherche d'un contrat d'alternance de 
                <span className="font-semibold text-primary-600 dark:text-primary-400"> 24 mois dès septembre 2025 </span> 
                (rythme : 1 semaine en formation / 2 semaines en entreprise). Mon objectif est d'allier 
                formation théorique et expérience pratique pour devenir un développeur accompli et 
                contribuer efficacement aux projets d'une équipe.
              </p>

              <motion.div 
                className="bg-gradient-to-r from-indigo-50 to-primary-50 dark:from-indigo-950/30 dark:to-primary-950/30 rounded-xl p-6 mt-6 border border-indigo-100/50 dark:border-indigo-800/30"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-gradient-to-r from-indigo-500 to-primary-500 rounded-full mr-3"></span>
                  Objectif professionnel
                </h4>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  Rejoindre une équipe dynamique pour développer des applications web innovantes, 
                  acquérir de l'expérience sur des projets concrets et monter en compétences 
                  sur les dernières technologies du développement web moderne.
                </p>
              </motion.div>

              <div className="grid grid-cols-2 gap-6 pt-6">
                <motion.div
                  whileHover={{ y: -2 }}
                  className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4"
                >
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Soft Skills</h4>
                  <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                      Créativité
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                      Curiosité
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                      Travail d'équipe
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                      Persévérance
                    </li>
                  </ul>
                </motion.div>
                
                <motion.div
                  whileHover={{ y: -2 }}
                  className="bg-slate-100 dark:bg-slate-800 rounded-xl p-4"
                >
                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Langues</h4>
                  <ul className="space-y-2 text-slate-600 dark:text-slate-300">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                      Français (C2)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                      Anglais (C1)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                      Portugais (B2)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-primary-500 rounded-full mr-3"></span>
                      Espagnol (B1)
                    </li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection direction="right" delay={0.4}>
            <div className="relative">
              <motion.div 
                className="bg-gradient-to-br from-indigo-600 to-primary-600 rounded-2xl p-8 text-white shadow-2xl border border-indigo-500/20"
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="grid grid-cols-2 gap-6">
                  <motion.div 
                    className="text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-4xl font-bold mb-2 bg-white/20 rounded-lg p-4 backdrop-blur-sm border border-white/10">
                      6+
                    </div>
                    <div className="text-indigo-100 font-medium">Projets réalisés</div>
                  </motion.div>
                  <motion.div 
                    className="text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-4xl font-bold mb-2 bg-white/20 rounded-lg p-4 backdrop-blur-sm border border-white/10">
                      2+
                    </div>
                    <div className="text-indigo-100 font-medium">Années d'expérience</div>
                  </motion.div>
                  <motion.div 
                    className="text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-4xl font-bold mb-2 bg-white/20 rounded-lg p-4 backdrop-blur-sm border border-white/10">
                      4
                    </div>
                    <div className="text-indigo-100 font-medium">Entreprises</div>
                  </motion.div>
                  <motion.div 
                    className="text-center"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-4xl font-bold mb-2 bg-white/20 rounded-lg p-4 backdrop-blur-sm border border-white/10">
                      8+
                    </div>
                    <div className="text-indigo-100 font-medium">Technologies maîtrisées</div>
                  </motion.div>
                </div>
                
                {/* Formation highlight */}
                <motion.div 
                  className="mt-8 bg-white/10 rounded-lg p-4 backdrop-blur-sm border border-white/10"
                  whileHover={{ scale: 1.02 }}
                >
                  <h4 className="font-semibold mb-2 text-center">Formation actuelle</h4>
                  <p className="text-indigo-100 text-center text-sm">
                    Bachelor Développeur Web & Application
                  </p>
                  <p className="text-indigo-200 text-center text-xs mt-1">
                    EFREI Paris • 2024-2025
                  </p>
                </motion.div>
                
                {/* Animated decorative elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 w-8 h-8 bg-white/20 rounded-full backdrop-blur-sm"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                />
                <motion.div 
                  className="absolute -bottom-4 -left-4 w-6 h-6 bg-white/20 rounded-full backdrop-blur-sm"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 4, delay: 1, ease: "easeInOut" }}
                />
              </motion.div>

              {/* Engagement associatif */}
              <motion.div 
                className="mt-6 bg-slate-100 dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700"
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ duration: 0.2 }}
              >
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mr-3"></span>
                  Engagement associatif
                </h4>
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                  Membre actif de l'association étudiante <strong>4eSport</strong>, participant aux événements 
                  organisés et favorisant l'esprit d'équipe autour de la passion pour l'e-sport.
                </p>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};